import * as yup from 'yup'
import { MakeRequiredAndNullable } from './utils.js'

export enum MezclaPurchaseOrderLineItemKeys {
  itemNumber = 'itemNumber', // line item number, not to be entered by user
  itemName = 'itemName', // name of the item, often SKU, Unique ID, reference ID, etc
  description = 'description', // verbose description of the item (human parseable)
  itemQty = 'itemQty', // quantity of the item for order
  caddiesQty = 'caddiesQty', // quantity of the item for order
  masterCasesQty = 'masterCasesQty', // quantity of the item for order
  unitPrice = 'unitPrice', // price per unit
  lineTotal = 'lineTotal', // total cost per (unitPrice * itemQty), not to be entered by user
}

// Used to create a summary of items grouped by item rather than SKU
export enum MezclaPurchaseOrderQuantitySummaryLineItemKeys {
  itemName = 'itemName', // name of the item, often SKU, Unique ID, reference ID, etc
  itemQty = 'itemQty', // quantity of the item for order
  unitOfMeasure = 'unitOfMeasure', // unit of measure for the item (i.e. jars or pallets)
}

export const mezclaPurchaseOrderFileDataSchema = yup.object().shape({
  issuer: yup.object().shape({
    attn: yup.string().nullable().optional(),
    companyName: yup.string().nullable().optional(),
    address: yup.string().nullable().optional(),
    phoneNumber: yup.string().nullable().optional(),
    emailAddress: yup.string().nullable().optional(),
  }),
  orderNumber: yup.string().nullable().optional(),
  dateIssued: yup.string().nullable().optional(),
  dateReady: yup.string().nullable().optional(),
  paymentTerms: yup.string().nullable().optional(),
  freightTerms: yup.string().nullable().optional(),
  destination: yup.object().shape({
    attn: yup.string().nullable().optional(),
    companyName: yup.string().nullable().optional(),
    address: yup.string().nullable().optional(),
    phoneNumber: yup.string().nullable().optional(),
    emailAddress: yup.string().nullable().optional(),
  }),
  vendor: yup.object().shape({
    attn: yup.string().nullable().optional(),
    companyName: yup.string().nullable().optional(),
    address: yup.string().nullable().optional(),
    phoneNumber: yup.string().nullable().optional(),
    emailAddress: yup.string().nullable().optional(),
  }),
  items: yup
    .array()
    .of(
      yup.object().shape({
        itemNumber: yup.number().nullable().optional(),
        itemName: yup.string().nullable().optional(),
        description: yup.string().nullable().optional(),
        itemQty: yup.string().nullable().optional(),
        caddiesQty: yup.string().nullable().optional(),
        masterCasesQty: yup.string().nullable().optional(),
        unitPrice: yup.string().nullable().optional(),
        lineTotal: yup.string().nullable().optional(),
      }),
    )
    .required(),
  quantitySummary: yup
    .array()
    .of(
      yup.object().shape({
        itemName: yup.string().nullable().optional(),
        itemQty: yup.string().nullable().optional(),
        unitOfMeasure: yup.string().nullable().optional(),
      }),
    )
    .nullable()
    .optional(),
  agreement: yup.string().nullable().optional(),
  instructions: yup.string().nullable().optional(),
})

export type MezclaPurchaseOrderFileData = yup.InferType<typeof mezclaPurchaseOrderFileDataSchema>
type RequiredFileData = MakeRequiredAndNullable<MezclaPurchaseOrderFileData>

export const mezclaPurchaseOrderFileMappings: RequiredFileData = {
  issuer: {
    attn: '71f3ba07-9167-461d-ae1c-fe24f9a8a94f',
    companyName: '1403e1da-a65a-4cb9-9c9e-2ba2145500fb',
    address: 'ebdbb207-31ed-4d9c-8c95-8e52185ba297',
    phoneNumber: '9de90f21-e195-40e2-87a8-bc66b790ad63',
    emailAddress: '5c51b7ca-4d5e-4409-a45a-3ce259477094',
  },
  orderNumber: '186e1200-5f0c-46f5-8d6a-7fccdd86cb70',
  paymentTerms: 'e9f1b231-bbe9-4fa2-aa07-acc27bcf775f',
  freightTerms: 'c7fbfc5f-3ed8-404c-ba4e-47df8917e970',
  dateIssued: '9e193cdd-71d3-42f2-bcba-e89ee68a0514',
  dateReady: '3f810f9d-0291-4f5a-acce-26fcdfffdc91',
  destination: {
    attn: 'f664dedc-40da-47b7-8561-3b69a44f4138',
    companyName: '31cb530c-639d-4b77-87ca-e1939251b5ca',
    address: '2ee3b079-a92a-4ff6-bd39-df765cf56315',
    phoneNumber: '73a004a0-f2e6-450a-97f2-0b9662cdfde8',
    emailAddress: 'c16f2e4a-0793-47b4-b503-641fd33ea45c',
  },
  vendor: {
    attn: '21b77b8b-d1db-4938-8f41-556ba9e2a5fe',
    companyName: 'daedaee2-8b48-4a1f-8830-e27287f2ef4e',
    address: '7ec447fd-24e6-4cb6-9eb3-f4aa92bfa1a8',
    phoneNumber: '0c970839-a2d4-48eb-ba8d-cc1ac4a5ddeb',
    emailAddress: '43a1e31e-ef76-4f5d-b493-7095a14ddbb8',
  },
  items: [
    {
      itemNumber: null,
      itemName: '4629697e-d400-472e-9695-9a33dd0c1407',
      description: 'b9842201-f03b-4e69-a1cb-dfba9c8429e1',
      itemQty: '322e5198-fa22-4665-8197-4124d13130cc',
      caddiesQty: '87b2f0ac-7c85-423c-9c24-c86595517c0d',
      masterCasesQty: '9de7e6ef-c005-4218-be5e-9d40632f4447',
      unitPrice: 'c224d53a-2c4c-44c4-a732-01315afcf03b',
      lineTotal: null,
    },
  ],
  quantitySummary: [
    {
      itemName: '6e4b7277-eb1d-4649-89c3-f8ca292e1f5e',
      itemQty: 'e7a83a9b-3217-40c5-8fdb-710ac625a8e5',
      unitOfMeasure: '592b615a-a212-4457-b1dc-49430b496428',
    },
  ],
  agreement: 'a1af320a-3f4f-439e-853e-39e74e300f72',
  instructions: '77da16b2-48bc-4f7d-ba4e-83c5e2609501',
}
